import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from '../../components/common/Modal';
import { shippingApi } from '../../api/shippingApi';
import { formatShareDate, formatShareNumber } from './supplierShareColumnDefs';
import { RequestFormPreview } from './RequestFormModal';
import { buildViewModelFromShipRequestDetail } from './shipRequestViewModel';

/**
 * 発注Noからかたまり候補を出し、一発発行する（社内）。
 */
export function ShipRequestIssueModal({ isOpen, hacchuNo, onClose, onIssued }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [issuingKey, setIssuingKey] = useState(null);
  const [previewDetail, setPreviewDetail] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setCandidates([]);
    setIssuingKey(null);
    setPreviewDetail(null);
    setExpandedKeys(new Set());
  }, []);

  useEffect(() => {
    if (!isOpen || !hacchuNo) {
      reset();
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setPreviewDetail(null);
      setExpandedKeys(new Set());
      try {
        const data = await shippingApi.getShipRequestCandidates(hacchuNo);
        if (!cancelled) setCandidates(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) {
          setCandidates([]);
          setError(e?.response?.data?.detail || e?.message || '候補の取得に失敗しました');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, hacchuNo, reset]);

  const toggleExpand = (clusterKey) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(clusterKey)) next.delete(clusterKey);
      else next.add(clusterKey);
      return next;
    });
  };

  const handleIssue = async (clusterKey) => {
    if (!hacchuNo || !clusterKey) return;
    setIssuingKey(clusterKey);
    setError(null);
    try {
      const detail = await shippingApi.issueShipRequest(hacchuNo, clusterKey);
      setPreviewDetail(detail);
      if (typeof onIssued === 'function') onIssued(detail);
      const refreshed = await shippingApi.getShipRequestCandidates(hacchuNo);
      setCandidates(Array.isArray(refreshed) ? refreshed : []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || '発行に失敗しました');
    } finally {
      setIssuingKey(null);
    }
  };

  const handlePrint = () => {
    const styleId = 'request-form-print-page-style';
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      styleEl.textContent = '@page { size: A4 portrait; margin: 8mm; }';
      document.head.appendChild(styleEl);
    }
    document.body.classList.add('request-form-print');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('request-form-print');
    }, 500);
  };

  const viewModel = previewDetail
    ? buildViewModelFromShipRequestDetail(previewDetail)
    : null;

  const hasOtherIssued = candidates.some((c) => c.already_issued);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={previewDetail ? '出荷依頼書プレビュー' : `出荷依頼の発行 — ${hacchuNo || ''}`}
      size="xl"
    >
      {loading ? (
        <p className="text-sm text-slate-600">読み込み中…</p>
      ) : error ? (
        <p className="text-sm text-red-600 whitespace-pre-wrap">{String(error)}</p>
      ) : null}

      {!previewDetail && !loading && (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            同一発送日・同一送り先のかたまりを選んで発行します。行をクリックすると対象明細を確認できます。
          </p>
          {hasOtherIssued ? (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              他日付の有効依頼あり（発送日ごとに有効依頼を持てます）
            </p>
          ) : null}
          {candidates.length === 0 ? (
            <p className="text-sm text-slate-500">
              発行可能なかたまりがありません（発送日のある明細が必要です）。
            </p>
          ) : (
            <ul className="divide-y divide-slate-200 border border-slate-200 rounded">
              {candidates.map((c) => {
                const expanded = expandedKeys.has(c.cluster_key);
                const lines = Array.isArray(c.lines) ? c.lines : [];
                return (
                  <li key={c.cluster_key} className="text-sm">
                    <div
                      role="button"
                      tabIndex={0}
                      className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50"
                      onClick={() => toggleExpand(c.cluster_key)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleExpand(c.cluster_key);
                        }
                      }}
                    >
                      <div className="min-w-0 space-y-0.5">
                        <div className="font-medium text-slate-900">
                          <span className="inline-block w-3 text-slate-400 mr-1">
                            {expanded ? '▾' : '▸'}
                          </span>
                          発送日 {formatShareDate(c.shipment_date)}
                          <span className="ml-2 text-slate-600">
                            {c.destination_name || '（送り先なし）'}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 pl-4">
                          {c.line_count}行
                          {c.total_weight != null
                            ? ` / ${formatShareNumber(Number(c.total_weight).toFixed(1))} kg`
                            : ''}
                          {c.already_issued ? ' / 発行済' : ''}
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={!!c.already_issued || issuingKey === c.cluster_key}
                        className="shrink-0 px-3 py-1.5 text-sm rounded bg-slate-900 text-white disabled:bg-slate-300"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleIssue(c.cluster_key);
                        }}
                      >
                        {c.already_issued
                          ? '発行済'
                          : issuingKey === c.cluster_key
                            ? '発行中…'
                            : '発行'}
                      </button>
                    </div>
                    {expanded && (
                      <div className="px-3 pb-2 pl-7">
                        {lines.length === 0 ? (
                          <p className="text-xs text-slate-500">明細情報がありません</p>
                        ) : (
                          <table className="w-full text-xs border border-slate-200 rounded overflow-hidden">
                            <thead className="bg-slate-50 text-slate-600">
                              <tr>
                                <th className="text-left px-2 py-1 font-medium">製品CD</th>
                                <th className="text-left px-2 py-1 font-medium">製品名</th>
                                <th className="text-right px-2 py-1 font-medium">数量</th>
                                <th className="text-right px-2 py-1 font-medium">分納</th>
                              </tr>
                            </thead>
                            <tbody>
                              {lines.map((ln, idx) => (
                                <tr key={ln.key || idx} className="border-t border-slate-100">
                                  <td className="px-2 py-1">{ln.product_disp || '-'}</td>
                                  <td className="px-2 py-1 max-w-[12rem] truncate" title={ln.product_name || ''}>
                                    {ln.product_name || '-'}
                                  </td>
                                  <td className="px-2 py-1 text-right whitespace-nowrap">
                                    {ln.qty != null
                                      ? `${formatShareNumber(ln.qty)}${ln.tani ? ` ${ln.tani}` : ''}`
                                      : '-'}
                                  </td>
                                  <td className="px-2 py-1 text-right">
                                    {ln.split_no ? ln.split_no : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              className="px-3 py-1.5 text-sm rounded border border-slate-300"
              onClick={onClose}
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {previewDetail && viewModel && (
        <div className="space-y-3">
          <p className="text-sm text-emerald-700">
            依頼No {previewDetail.request_no} を発行しました。
          </p>
          <div className="request-form-print-root overflow-auto max-h-[60vh] border border-slate-200 rounded p-4 bg-white">
            <RequestFormPreview viewModel={viewModel} />
          </div>
          <div className="request-form-no-print flex flex-wrap justify-end gap-2">
            <button
              type="button"
              className="px-3 py-1.5 text-sm rounded border border-slate-300"
              onClick={() => setPreviewDetail(null)}
            >
              候補に戻る
            </button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm rounded border border-slate-300"
              onClick={onClose}
            >
              閉じる
            </button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm rounded bg-slate-900 text-white"
              onClick={handlePrint}
            >
              印刷
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
